import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Ban } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { Button } from '../components/core/Button';
import { LanguageToggle } from '../components/core/LanguageToggle';
import { POST_LOGIN_PATH } from '../constants/dashboard';

export function LoginPage() {
  const { user, loading, signInWithGoogle } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [oauthError, setOauthError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && user) navigate(POST_LOGIN_PATH, { replace: true });
  }, [user, loading, navigate]);

  /**
   * Supabase reports a refused login by redirecting back here with the reason in
   * the URL fragment — a banned user's Google sign-in never reaches our API, so
   * this is the only place that failure can be explained.
   *
   * The fragment is cleared afterwards so a reload does not resurrect a stale
   * error, matching what `detectSessionInUrl` does with the auth code.
   */
  useEffect(() => {
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
    if (!hash) return;

    const params = new URLSearchParams(hash);
    const description = params.get('error_description');
    if (!description && !params.get('error')) return;

    setOauthError(description ?? params.get('error'));
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Signed-out users land here first, so this is the only place the language
            can be chosen before an account exists. */}
        <div className="flex justify-end mb-4">
          <LanguageToggle variant="segmented" />
        </div>

        {/* Logo */}
        <div className="flex flex-col items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-violet-600 flex items-center justify-center shadow-lg">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white">{t('login.title')}</h1>
            <p className="text-slate-400 text-sm mt-1">{t('login.subtitle')}</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 shadow-xl">
          <h2 className="text-xl font-semibold text-white text-center mb-2">
            {t('login.welcome')}
          </h2>
          <p className="text-slate-400 text-sm text-center mb-8">{t('login.description')}</p>

          {oauthError && (
            <div
              role="alert"
              className="mb-6 flex items-start gap-3 rounded-xl border border-rose-500/40 bg-rose-500/10 p-4"
            >
              <Ban className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
              <div>
                {/* Supabase says "User is banned" and nothing more — no reason, no
                    expiry — so the wording here stays general and points at support
                    rather than inventing detail the redirect does not carry. */}
                <p className="text-sm font-semibold text-rose-300">
                  {oauthError.toLowerCase().includes('banned')
                    ? t('login.suspendedTitle')
                    : t('login.failedTitle')}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {oauthError.toLowerCase().includes('banned')
                    ? t('login.suspendedBody')
                    : oauthError}
                </p>
              </div>
            </div>
          )}

          <Button
            onClick={signInWithGoogle}
            className="w-full flex items-center justify-center gap-3 bg-white text-slate-900 hover:bg-slate-100 font-medium py-3 rounded-xl"
          >
            {/* Google SVG icon */}
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('login.google')}
          </Button>

          <p className="text-slate-500 text-xs text-center mt-6">{t('login.terms')}</p>
        </div>
      </div>
    </div>
  );
}
